import React, { useState, useRef, useEffect } from 'react';
import { Flipper, Flipped } from 'react-flip-toolkit';
import VisibilitySensor from 'react-visibility-sensor';
import cn from 'classnames';
import ReactPlayer from 'react-player';
import Image from 'next/image';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';

import styles from './gallery.module.css';

const Caption = ({ timeShow, caption }) =>
  caption?.map(cc => {
    const captionStyles = cn(
      styles.quote,
      'bg-black pt-2 pb-6 lg:pl-4 transition-opacity duration-150 ease-in-out absolute bg-black',
      cc.time === 0 ? 'relative' : 'absolute max-h-max inset-0',
      timeShow >= cc.time ? 'opacity-100' : 'opacity-0'
    );
    const captionInnerStyles = cn(
      'transform transition-transform duration-700 ease-in-out block',
      timeShow >= cc.time ? 'translate-x-0' : 'translate-x-3'
    );

    return (
      <p key={cc.time} className={captionStyles}>
        <span className={captionInnerStyles}>{timeShow >= cc.time && cc.text}</span>
      </p>
    );
  });

const Gallery = ({
  tabItems,
  theme = 'dark',
  duration = 9000,
  gradientColor = '0, 0, 0',
  videoAutoPlay = true
}) => {
  const [activeTab, setActiveTab] = useState(tabItems[0]);
  const [start, setStart] = useState(false);
  // const [timeShow, setTimeShow] = useState(0);
  const current = activeTab;
  const currentIndex = tabItems.findIndex(tabItem => tabItem === current);
  const playerRef = useRef(null);
  const [overflow, setOverflow] = useState({ overflowLeft: false, overflowRight: false });
  const ref = useRef(null);

  useEffect(() => {
    if (!start || tabItems.length < 2) return;

    const interval = setInterval(
      () => setActiveTab(tabItems[currentIndex + 1] ?? tabItems[0]),
      duration
    );

    return () => {
      clearInterval(interval);
    };
  }, [activeTab, start]);

  useEffect(() => {
    if (!ref.current) return;

    const tabsRef = ref?.current;

    const handleResizeOrScroll = () => {
      const { scrollWidth, offsetWidth, scrollLeft } = tabsRef;
      const spaceLeft = offsetWidth + scrollLeft;

      if (scrollWidth === offsetWidth) {
        setOverflow({ overflowLeft: false, overflowRight: false });
      } else if (spaceLeft === scrollWidth) {
        setOverflow({ overflowLeft: true, overflowRight: false });
      } else if (spaceLeft === offsetWidth) {
        setOverflow({ overflowLeft: false, overflowRight: true });
      } else {
        setOverflow({ overflowLeft: true, overflowRight: true });
      }
    };

    const resizeObserver = new ResizeObserver(handleResizeOrScroll);

    tabsRef.addEventListener('scroll', handleResizeOrScroll);
    resizeObserver.observe(tabsRef);

    handleResizeOrScroll();

    return () => {
      tabsRef?.removeEventListener('scroll', handleResizeOrScroll);
      resizeObserver.disconnect();
    };
  }, [ref]);

  const onVisibilityChange = isVisible => setStart(isVisible);

  const gradientStyles = {
    left: cn(
      'pointer-events-none absolute left-0 top-0 bottom-[1px] z-20 w-1/5 opacity-0 transition-opacity duration-300',
      overflow.overflowLeft && 'opacity-100'
    ),
    right: cn(
      'pointer-events-none absolute right-0 top-0 bottom-[1px] z-20 w-1/5 opacity-0 transition-opacity duration-300',
      overflow.overflowRight && 'opacity-100'
    )
  };

  return (
    <>
      <VisibilitySensor onChange={onVisibilityChange} scrollCheck>
        <div className={cn(styles.tabGrid, 'grid gap-14')}>
          <div className="hidden lg:block" />
          <div className="relative overflow-auto">
            <div
              style={{
                background: `linear-gradient(to left, rgba(0, 0, 0, 0), rgb(${gradientColor})`
              }}
              className={gradientStyles.left}
            />
            <div
              style={{
                background: `linear-gradient(to right, rgba(0, 0, 0, 0), rgb(${gradientColor})`
              }}
              className={gradientStyles.right}
            />
            <div ref={ref} className="overflow-auto">
              <div className="md:min-w-full">
                <Flipper flipKey={activeTab}>
                  <div className="flex md:gap-x-6 xl:gap-x-12">
                    {tabItems?.map(t => (
                      <button
                        key={t.label}
                        onClick={() => {
                          setActiveTab(t);
                          setStart(false);
                        }}
                        type="button"
                        className="relative"
                      >
                        {activeTab?.label === t.label && (
                          <Flipped flipId="tabBg">
                            <div
                              className="absolute inset-0 tabBg bg-scale-mauve rounded-3xl"
                              style={{ willChange: 'transform' }}
                            />
                          </Flipped>
                        )}
                        <div
                          className={cn(
                            activeTab === t ? 'text-white' : textTheme[theme],
                            'relative z-10 text-sm py-1.5 px-4 transition-colors duration-200 ease-in-out whitespace-nowrap'
                          )}
                        >
                          {t.label}
                        </div>
                      </button>
                    ))}
                  </div>
                </Flipper>
              </div>
            </div>
          </div>
          <div
            className={cn(
              textTheme[theme],
              'hidden lg:block text-sm md:text-base leading-loose relative'
            )}
          >
            {activeTab?.caption ? (
              <Caption
                caption={activeTab.caption}
                // timeShow={timeShow}
              />
            ) : (
              <div className={cn('py-2 lg:py-0 relative')}>
                <p>{activeTab?.text}</p>
                {activeTab?.bullets?.length > 0 && (
                  <ul className="space-y-6">
                    {activeTab.bullets?.map((bullet, index) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <li key={index} className="flex items-start space-x-4">
                        <picture className={cn('relative', bullet?.title ? 'top-1' : 'top-0.5')}>
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <circle cx="12" cy="12" r="12" fill="#D1AAD7" />
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M17.3787 6.53657C17.7599 6.22599 18.3246 6.19775 18.7199 6.52246C19.1716 6.90363 19.2281 7.56716 18.8328 8.01893L11.3787 16.9695C11.2516 17.1248 11.1105 17.266 10.9411 17.3789C9.96694 18.0283 8.654 17.7601 8.00458 16.786L5.06811 12.3813C4.74341 11.9013 4.88458 11.2378 5.36458 10.913C5.73164 10.673 6.19752 10.6872 6.53635 10.913L9.62811 12.9601L17.3646 6.52246L17.3787 6.53657Z"
                              fill="black"
                            />
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24ZM12 24C5.37882 24 0 18.6212 0 12C0 5.37882 5.37882 0 12 0C18.6212 0 24 5.37882 24 12C24 18.6212 18.6212 24 12 24Z"
                              fill="#E5E5E5"
                            />
                          </svg>
                        </picture>
                        <div className="flex-1">
                          {bullet?.title && (
                            <h3 className="text-white text-xl font-aeonik mb-2">{bullet?.title}</h3>
                          )}
                          <ReactMarkdown
                            rehypePlugins={[rehypeRaw]}
                            className="prose-a:underline prose-a:underline-offset-[6px] prose-a:decoration-1"
                          >
                            {bullet.text}
                          </ReactMarkdown>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
          <div className="overflow-hidden rounded-lg">
            <div className="relative aspect-video">
              {activeTab?.video && (
                <ReactPlayer
                  ref={playerRef}
                  className="absolute top-0 left-0"
                  url={activeTab.video.src}
                  playing={start && videoAutoPlay}
                  controls
                  pip={false}
                  width="100%"
                  height="100%"
                  // onPlay={showCurrentTime}
                  // onEnded={onNextVideo}
                />
              )}
              {activeTab?.image?.src && (
                <Image
                  className="rounded-lg"
                  src={activeTab?.image?.src}
                  alt={activeTab?.image?.alt}
                  layout={activeTab?.image.width ? 'responsive' : 'fill'}
                  width={activeTab?.image.width}
                  height={activeTab.image.height}
                  quality={100}
                />
              )}
            </div>
            <div
              className={cn(
                textTheme[theme],
                'lg:hidden text-sm md:text-base leading-loose relative mt-12 '
              )}
            >
              {activeTab?.caption ? (
                <Caption
                  caption={activeTab.caption}
                  // timeShow={timeShow}
                />
              ) : (
                <div className={cn('pt-10 relative')}>
                  <p>{activeTab?.text}</p>
                  {activeTab?.bullets?.length > 0 && (
                    <ul className="space-y-6">
                      {activeTab.bullets?.map((bullet, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <li key={index} className="flex items-start space-x-4">
                          <picture className="relative top-[2px]">
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <circle cx="12" cy="12" r="12" fill="#D1AAD7" />
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M17.3787 6.53657C17.7599 6.22599 18.3246 6.19775 18.7199 6.52246C19.1716 6.90363 19.2281 7.56716 18.8328 8.01893L11.3787 16.9695C11.2516 17.1248 11.1105 17.266 10.9411 17.3789C9.96694 18.0283 8.654 17.7601 8.00458 16.786L5.06811 12.3813C4.74341 11.9013 4.88458 11.2378 5.36458 10.913C5.73164 10.673 6.19752 10.6872 6.53635 10.913L9.62811 12.9601L17.3646 6.52246L17.3787 6.53657Z"
                                fill="black"
                              />
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24ZM12 24C5.37882 24 0 18.6212 0 12C0 5.37882 5.37882 0 12 0C18.6212 0 24 5.37882 24 12C24 18.6212 18.6212 24 12 24Z"
                                fill="#E5E5E5"
                              />
                            </svg>
                          </picture>
                          <p className="flex-1">
                            <ReactMarkdown
                              rehypePlugins={[rehypeRaw]}
                              className="prose-a:underline prose-a:underline-offset-[6px] prose-a:decoration-1"
                            >
                              {bullet.text}
                            </ReactMarkdown>
                          </p>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </VisibilitySensor>
    </>
  );
};

const textTheme = {
  dark: 'text-white',
  light: 'text-black'
};

export default Gallery;
